import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';

const MAX_RANGE_DAYS = 90;

function toDateOnly(d) {
  const x = new Date(d);
  return x.toISOString().slice(0, 10);
}

function today() {
  return toDateOnly(new Date());
}

function nDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toDateOnly(d);
}

function threeMonthsAgo() {
  const d = new Date();
  d.setDate(d.getDate() - MAX_RANGE_DAYS);
  return toDateOnly(d);
}

const DATE_PRESETS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'last_1_week', label: 'Last 1 week' },
  { id: 'last_30_days', label: 'Last 30 days' },
];

function getRangeForPreset(presetId) {
  const todayStr = today();
  switch (presetId) {
    case 'today':
      return { start: todayStr, end: todayStr };
    case 'yesterday': {
      const y = nDaysAgo(1);
      return { start: y, end: y };
    }
    case 'last_1_week':
      return { start: nDaysAgo(6), end: todayStr };
    case 'last_30_days':
      return { start: nDaysAgo(29), end: todayStr };
    default:
      return null;
  }
}

const STAT_TYPES = [
  { key: 'total', label: 'Total records', sublabel: 'Workflow + Manual', color: '#3498db' },
  { key: 'running', label: 'Records currently processing', sublabel: 'Active runs', color: '#f39c12' },
  { key: 'completed', label: 'Records completed successfully', sublabel: 'Successful', color: '#27ae60' },
  { key: 'failed', label: 'Records that failed', sublabel: 'Records not executed', color: '#e74c3c' },
];

function formatLabel(key) {
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^\s+|\s+$/g, '')
    .replace(/\s+/g, ' ')
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function formatValue(val) {
  if (val == null) return '—';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}

const ENRICHMENT_BOX_CONFIG = [
  { key: 'ai_count', altKey: 'aiCount', label: 'AI Enrichments', color: '#7c3aed', bg: '#f5f3ff', icon: '◇' },
  { key: 'email_count', altKey: 'emailCount', label: 'Email Enrichments', color: '#2563eb', bg: '#eff6ff', icon: '✉' },
  { key: 'phone_count', altKey: 'phoneCount', label: 'Phone Enrichments', color: '#0d9488', bg: '#f0fdfa', icon: '☎' },
  { key: 'basic_count', altKey: 'basicCount', label: 'Basic Enrichments', color: '#d97706', bg: '#fffbeb', icon: '◆' },
  { key: 'other_count', altKey: 'otherCount', label: 'Other Enrichments', color: '#475569', bg: '#f8fafc', icon: '○' },
];

function isEnrichmentSummary(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
  const keys = Object.keys(obj).map((k) => k.toLowerCase().replace(/_/g, ''));
  const required = ['aicount', 'emailcount', 'phonecount', 'basiccount', 'othercount'];
  return required.every((r) => keys.some((k) => k === r));
}

function getEnrichmentValue(obj, config) {
  const v = obj[config.key] ?? obj[config.altKey];
  return v != null ? Number(v) : 0;
}

function renderEnrichmentBoxes(obj) {
  if (!obj || typeof obj !== 'object') return null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '16px' }}>
      {ENRICHMENT_BOX_CONFIG.map((config) => {
        const value = getEnrichmentValue(obj, config);
        return (
          <div
            key={config.key}
            style={{
              padding: '20px 18px',
              borderRadius: '14px',
              backgroundColor: config.bg,
              border: `1px solid ${config.color}20`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              textAlign: 'center',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.08)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
            }}
          >
            <div style={{ fontSize: '20px', marginBottom: '8px', color: config.color, lineHeight: 1 }}>{config.icon}</div>
            <div style={{ fontSize: '12px', fontWeight: 600, color: config.color, letterSpacing: '0.02em', marginBottom: '6px', textTransform: 'uppercase' }}>
              {config.label}
            </div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
              {typeof value === 'number' ? value.toLocaleString() : formatValue(value)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function renderDataCards(items) {
  if (!Array.isArray(items) || items.length === 0) return null;
  const first = items[0];
  if (typeof first !== 'object' || first === null) {
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {items.map((v, i) => (
          <span key={i} style={{ padding: '6px 12px', backgroundColor: '#f1f5f9', borderRadius: '8px', fontSize: '13px', color: '#334155' }}>
            {formatValue(v)}
          </span>
        ))}
      </div>
    );
  }
  const keys = Object.keys(first);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
      {items.map((item, idx) => (
        <div
          key={idx}
          style={{
            padding: '14px 16px',
            backgroundColor: '#f8fafc',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          }}
        >
          {keys.map((k) => (
            <div key={k} style={{ marginBottom: keys.indexOf(k) < keys.length - 1 ? '10px' : 0 }}>
              <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '2px' }}>
                {formatLabel(k)}
              </div>
              <div style={{ fontSize: '14px', fontWeight: 500, color: '#0f172a' }}>{formatValue(item[k])}</div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function renderDataContent(data) {
  if (data == null) return null;
  if (Array.isArray(data)) {
    if (data.length === 1 && isEnrichmentSummary(data[0])) return renderEnrichmentBoxes(data[0]);
    return renderDataCards(data);
  }
  if (typeof data === 'object') {
    if (isEnrichmentSummary(data)) return renderEnrichmentBoxes(data);
    return renderDataCards([data]);
  }
  return <span style={{ fontSize: '14px', color: '#334155' }}>{formatValue(data)}</span>;
}

export default function WorkflowTaskAnalytics() {
  const [selectedPreset, setSelectedPreset] = useState('today');
  const [environment, setEnvironment] = useState('production');
  const [start, setStart] = useState(today());
  const [end, setEnd] = useState(today());
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorDistributionList, setErrorDistributionList] = useState([]);
  const [errorDistributionLoading, setErrorDistributionLoading] = useState(false);
  const [selectedView, setSelectedView] = useState(null);
  const [selectedErrorReason, setSelectedErrorReason] = useState(null);
  const [recordLogs, setRecordLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPagination, setLogsPagination] = useState({ limit: 50, offset: 0, total: null });
  const [loadingMoreLogs, setLoadingMoreLogs] = useState(false);
  const [showEnrichmentsModal, setShowEnrichmentsModal] = useState(false);
  const [enrichmentsStatType, setEnrichmentsStatType] = useState(null);
  const [overallVendorsData, setOverallVendorsData] = useState(null);
  const [overallVendorsLoading, setOverallVendorsLoading] = useState(false);
  const [expandedRowKey, setExpandedRowKey] = useState(null);
  const [breakdownByKey, setBreakdownByKey] = useState({});
  const [loadingBreakdownFor, setLoadingBreakdownFor] = useState(null);

  const range = { start, end };
  const minStart = threeMonthsAgo();

  const setPresetAndRange = (presetId) => {
    setSelectedPreset(presetId);
    const rangeForPreset = getRangeForPreset(presetId);
    if (rangeForPreset) {
      setStart(rangeForPreset.start);
      setEnd(rangeForPreset.end);
    }
  };

  const handleStartChange = (value) => {
    setSelectedPreset(null);
    setStart(value);
  };

  const handleEndChange = (value) => {
    setSelectedPreset(null);
    setEnd(value);
  };

  const rangeParams = () => {
    const startDate = new Date(`${range.start}T00:00:00`);
    const endDate = new Date(`${range.end}T23:59:59.999`);
  
    return {
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      environment,
    };
  };

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/workflow-task-analytics/stats', {
        params: rangeParams(),
      });
      setStats(res.data?.data || null);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load stats');
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [range.start, range.end, environment]);

  const fetchErrorDistribution = useCallback(async () => {
    setErrorDistributionLoading(true);
    try {
      const res = await api.get('/api/workflow-task-analytics/error-distribution', {
        params: rangeParams(),
      });
      const data = res.data?.data;
      const list = Array.isArray(data)
        ? data
        : (data?.data || data?.errorDistribution || []);
      setErrorDistributionList(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Failed to load error distribution', err);
      setErrorDistributionList([]);
    } finally {
      setErrorDistributionLoading(false);
    }
  }, [range.start, range.end, environment]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchErrorDistribution();
  }, [fetchErrorDistribution]);

  const fetchRecordLogs = useCallback(
    async (statType, reason, resetOffset = true) => {
      const offset = resetOffset ? 0 : logsPagination.offset;
      const limit = logsPagination.limit;
      if (resetOffset) setLogsLoading(true);
      else setLoadingMoreLogs(true);
      try {
        const params = {
          ...rangeParams(),
          statType,
          limit,
          offset,
        };
        if (reason) params.reason = reason;
        const res = await api.get('/api/workflow-task-analytics/logs/records', { params });
        const payload = res.data?.data || {};
        const list = Array.isArray(payload) ? payload : (payload.data || []);
        const totalFromApi = payload.pagination?.total;
        const total = totalFromApi != null ? totalFromApi : (list.length < limit ? offset + list.length : null);
        if (resetOffset) {
          setRecordLogs(list);
          setLogsPagination((p) => ({ ...p, offset: list.length, total }));
        } else {
          setRecordLogs((prev) => [...prev, ...list]);
          setLogsPagination((p) => ({ ...p, offset: offset + list.length, total: total != null ? total : offset + list.length }));
        }
      } catch (err) {
        console.error('Failed to load record logs', err);
        if (resetOffset) setRecordLogs([]);
      } finally {
        setLogsLoading(false);
        setLoadingMoreLogs(false);
      }
    },
    [range.start, range.end, environment, logsPagination.limit, logsPagination.offset]
  );

  const handleBoxClick = (statKey) => {
    setSelectedView(statKey);
    setSelectedErrorReason(null);
    setRecordLogs([]);
    setLogsPagination((p) => ({ ...p, offset: 0, total: null }));
    setExpandedRowKey(null);
    setBreakdownByKey({});
    fetchRecordLogs(statKey, null, true);
  };

  const handleViewByWorkspace = (e, statKey) => {
    e.preventDefault();
    e.stopPropagation();
    handleBoxClick(statKey);
  };

  const fetchOverallVendors = useCallback(async (statType, reason = null) => {
    setOverallVendorsLoading(true);
    setEnrichmentsStatType(statType);
    setShowEnrichmentsModal(true);
    setOverallVendorsData(null);
    try {
      const params = { ...rangeParams(), statType };
      if (reason) params.reason = reason;
      const res = await api.get('/api/workflow-task-analytics/overall-vendors', { params });
      const data = res.data?.data;
      setOverallVendorsData(Array.isArray(data) ? data : (data?.data || data?.vendors || []));
    } catch (err) {
      console.error('Failed to load overall vendors', err);
      setOverallVendorsData([]);
    } finally {
      setOverallVendorsLoading(false);
    }
  }, [range.start, range.end, environment]);

  const handleEnrichmentsClick = (e, statKey) => {
    e.preventDefault();
    e.stopPropagation();
    fetchOverallVendors(statKey);
  };

  const handleEnrichmentsClickFromErrorDistribution = (e, reason = null) => {
    e.preventDefault();
    e.stopPropagation();
    const toUse = reason || selectedErrorReason;
    if (!toUse) return;
    fetchOverallVendors('errordistribution', toUse);
  };

  const fetchWorkspaceEnrichmentBreakdown = useCallback(async (slugOrAccountId) => {
    setExpandedRowKey(slugOrAccountId);
    setLoadingBreakdownFor(slugOrAccountId);
    try {
      const params = {
        ...rangeParams(),
        statType: selectedView === 'errorDistribution' ? 'errordistribution' : selectedView,
        accountId: slugOrAccountId,
      };
      if (selectedView === 'errorDistribution' && selectedErrorReason) params.reason = selectedErrorReason;
      const res = await api.get('/api/workflow-task-analytics/workspace-enrichment-breakdown', { params });
      const data = res.data?.data;
      const list = Array.isArray(data) ? data : (data?.data || data?.breakdown || []);
      setBreakdownByKey((prev) => ({ ...prev, [slugOrAccountId]: list }));
    } catch (err) {
      console.error('Failed to load workspace enrichment breakdown', err);
      setBreakdownByKey((prev) => ({ ...prev, [slugOrAccountId]: null }));
    } finally {
      setLoadingBreakdownFor(null);
    }
  }, [range.start, range.end, environment, selectedView, selectedErrorReason]);

  const toggleRowExpand = (e, row) => {
    e.stopPropagation();
    const key = row.account_id ?? row.slug ?? row.accountId;
    if (!key) return;
    if (expandedRowKey === key) {
      setExpandedRowKey(null);
      return;
    }
    if (breakdownByKey[key] !== undefined) {
      setExpandedRowKey(key);
      return;
    }
    fetchWorkspaceEnrichmentBreakdown(key);
  };

  const handleErrorDistributionClick = (reason) => {
    setSelectedView('errorDistribution');
    setSelectedErrorReason(reason);
    setRecordLogs([]);
    setLogsPagination((p) => ({ ...p, offset: 0, total: null }));
    fetchRecordLogs('errordistribution', reason, true);
  };

  const loadMoreLogs = () => {
    if (loadingMoreLogs || !selectedView) return;
    const reason = selectedView === 'errorDistribution' ? selectedErrorReason : null;
    const statType = selectedView === 'errorDistribution' ? 'errordistribution' : selectedView;
    fetchRecordLogs(statType, reason, false);
  };

  const getCountColumnLabel = () => {
    if (!selectedView) return 'Count';
    if (selectedView === 'total') return 'Total records';
    if (selectedView === 'running') return 'Running records';
    if (selectedView === 'completed') return 'Completed records';
    if (selectedView === 'failed') return 'Failed records';
    return 'Count';
  };

  const getStatValue = (key) => {
    if (!stats) return '—';
    return stats[key] ?? '—';
  };

  const showEnrichmentBreakdown =
    selectedView === 'running' ||
    selectedView === 'completed' ||
    (selectedView === 'errorDistribution' && selectedErrorReason === 'Enrichment failed');
  useEffect(() => {
    if (!showEnrichmentBreakdown) {
      setExpandedRowKey(null);
    }
  }, [showEnrichmentBreakdown]);

  const DRILLDOWN_CONTEXT_LINE = 'Within selected date range • Grouped by workspace';

  const getDrillDownHeading = () => {
    if (!selectedView) return '';
    if (selectedView === 'errorDistribution') return `Workflow step failures — ${selectedErrorReason || 'Error distribution'}`;
    if (selectedView === 'total') return 'Records processed (Workflow + Manual)';
    if (selectedView === 'running') return 'Records currently processing';
    if (selectedView === 'completed') return 'Records completed successfully';
    if (selectedView === 'failed') return 'Records that failed';
    return selectedView;
  };

  const errorDistribution = errorDistributionList;
  const maxErrorCount = errorDistribution.length > 0
    ? Math.max(...errorDistribution.map((e) => e.count || 0), 1)
    : 1;

  return (
    <div
      style={{
        padding: '24px',
        backgroundColor: '#ffffff',
        minHeight: '100vh',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        color: '#1f2937',
        overflowX: 'hidden',
      }}
    >
      <header
        style={{
          marginBottom: '28px',
          paddingBottom: '24px',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        <h1 style={{ margin: 0, fontSize: '26px', fontWeight: 600, color: '#111827', letterSpacing: '-0.02em' }}>
          Workflow task analytics
        </h1>
        <p style={{ margin: '6px 0 0', fontSize: '14px', color: '#6b7280' }}>
          Totals and status breakdown for workflow and manual executions in the selected range.
        </p>

        <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '13px', color: '#495057', fontWeight: '600' }}>Environment:</span>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              type="button"
              onClick={() => setEnvironment('production')}
              style={{
                padding: '4px 12px',
                borderRadius: '4px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: environment === 'production' ? '600' : '400',
                backgroundColor: environment === 'production' ? '#007bff' : 'transparent',
                color: environment === 'production' ? '#fff' : '#495057',
                transition: 'all 0.2s',
                height: '24px',
              }}
              onMouseEnter={(e) => {
                if (environment !== 'production') e.currentTarget.style.backgroundColor = '#e9ecef';
              }}
              onMouseLeave={(e) => {
                if (environment !== 'production') e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              Production
            </button>
            <button
              type="button"
              onClick={() => setEnvironment('staging')}
              style={{
                padding: '4px 12px',
                borderRadius: '4px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: environment === 'staging' ? '600' : '400',
                backgroundColor: environment === 'staging' ? '#007bff' : 'transparent',
                color: environment === 'staging' ? '#fff' : '#495057',
                transition: 'all 0.2s',
                height: '24px',
              }}
              onMouseEnter={(e) => {
                if (environment !== 'staging') e.currentTarget.style.backgroundColor = '#e9ecef';
              }}
              onMouseLeave={(e) => {
                if (environment !== 'staging') e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              Staging
            </button>
          </div>
        </div>

        {/* Calendar dates at top – always clickable */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            marginTop: '20px',
            flexWrap: 'wrap',
            padding: '16px 20px',
            backgroundColor: '#f8fafc',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
          }}
        >
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: '#334155', fontWeight: 500 }}>
            <span>From</span>
            <input
              type="date"
              value={start}
              min={minStart}
              max={end}
              onChange={(e) => handleStartChange(e.target.value)}
              style={{
                padding: '10px 14px',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                backgroundColor: '#fff',
                color: '#0f172a',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                outline: 'none',
              }}
              onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px #3b82f6'}
              onBlur={(e) => e.target.style.boxShadow = 'none'}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: '#334155', fontWeight: 500 }}>
            <span>To</span>
            <input
              type="date"
              value={end}
              min={start}
              max={today()}
              onChange={(e) => handleEndChange(e.target.value)}
              style={{
                padding: '10px 14px',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                backgroundColor: '#fff',
                color: '#0f172a',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                outline: 'none',
              }}
              onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px #3b82f6'}
              onBlur={(e) => e.target.style.boxShadow = 'none'}
            />
          </label>
          <span style={{ fontSize: '12px', color: '#64748b' }}>
            Max range: 3 months (from {minStart})
          </span>
        </div>

        {/* Preset buttons below – none selected when calendar was used */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '16px', flexWrap: 'wrap' }}>
          {DATE_PRESETS.map((preset) => {
            const isSelected = selectedPreset === preset.id;
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => setPresetAndRange(preset.id)}
                style={{
                  padding: '10px 18px',
                  fontSize: '13px',
                  fontWeight: isSelected ? 600 : 500,
                  color: isSelected ? '#fff' : '#475569',
                  background: isSelected ? '#0f172a' : '#fff',
                  border: `1px solid ${isSelected ? '#0f172a' : '#e2e8f0'}`,
                  borderRadius: '9999px',
                  cursor: 'pointer',
                  boxShadow: isSelected ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                  transition: 'color 0.15s, background 0.15s, border-color 0.15s, box-shadow 0.15s',
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = '#f1f5f9';
                    e.currentTarget.style.borderColor = '#cbd5e1';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = '#fff';
                    e.currentTarget.style.borderColor = '#e2e8f0';
                  }
                }}
              >
                {preset.label}
              </button>
            );
          })}
        </div>
      </header>

      {error && (
        <div
          style={{
            padding: '12px 16px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            marginBottom: '20px',
            color: '#dc2626',
          }}
        >
          {error}
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
          <div
            style={{
              display: 'inline-block',
              width: '32px',
              height: '32px',
              border: '3px solid #e5e7eb',
              borderTopColor: '#3b82f6',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
            }}
          />
          <p style={{ marginTop: '12px' }}>Loading stats…</p>
        </div>
      )}

      {!loading && stats && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: '20px',
          }}
        >
          {STAT_TYPES.map(({ key, label, sublabel, color }) => {
            const isSelected = selectedView === key;
            const showEnrichmentsLink = key === 'running' || key === 'completed';
            return (
              <div
                key={key}
                style={{
                  background: isSelected ? `${color}08` : '#fff',
                  border: `1px solid ${isSelected ? color : '#e5e7eb'}`,
                  borderRadius: '14px',
                  padding: '20px',
                  minHeight: '140px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  boxShadow: isSelected ? `0 0 0 2px ${color}40` : '0 1px 3px rgba(0,0,0,0.06)',
                  transition: 'box-shadow 0.2s, border-color 0.2s',
                }}
              >
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{sublabel}</div>
                  <div style={{ fontSize: '28px', fontWeight: 700, color, lineHeight: 1.2 }}>{getStatValue(key)}</div>
                  <div style={{ fontSize: '14px', color: '#374151', marginTop: '6px' }}>{label}</div>
                </div>
                <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <a
                    href="#view-by-workspace"
                    onClick={(e) => handleViewByWorkspace(e, key)}
                    style={{
                      fontSize: '12px',
                      color: '#2563eb',
                      textDecoration: 'none',
                      fontWeight: 500,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                  >
                    Click to view by workspace
                  </a>
                  {showEnrichmentsLink && (
                    <a
                      href="#enrichments"
                      onClick={(e) => handleEnrichmentsClick(e, key)}
                      style={{
                        fontSize: '12px',
                        color: '#059669',
                        textDecoration: 'none',
                        fontWeight: 500,
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                    >
                      Click to know about enrichments
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && stats && (errorDistributionLoading || errorDistribution.length > 0) && (
        <section style={{ marginTop: '28px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px', color: '#111827', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#f59e0b', fontSize: '20px' }} title="Error distribution">⚠</span>
            Error distribution
          </h2>
          <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 12px 0', fontStyle: 'italic' }}>
            This shows failures of tasks or nodes within workflows. A single workflow run may fail at multiple tasks or nodes.
          </p>
          {errorDistributionLoading ? (
            <div style={{ padding: '16px', color: '#6b7280', fontSize: '14px' }}>Loading error distribution…</div>
          ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {errorDistribution.map(({ reason, count }, i) => (
              <div
                key={i}
                onClick={() => handleErrorDistributionClick(reason)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid transparent',
                  transition: 'background-color 0.15s, border-color 0.15s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = 'transparent';
                }}
              >
                <span style={{ fontSize: '14px', color: '#1f2937', minWidth: '180px', flexShrink: 0 }}>
                  {reason || '(empty)'}
                </span>
                <div
                  style={{
                    flex: 1,
                    height: '20px',
                    backgroundColor: '#e5e7eb',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${Math.min(100, (count / maxErrorCount) * 100)}%`,
                      backgroundColor: '#dc2626',
                      borderRadius: '4px',
                      transition: 'width 0.3s ease',
                    }}
                  />
                </div>
                <span style={{ fontSize: '14px', fontWeight: 600, color: '#1f2937', minWidth: '48px', textAlign: 'right' }}>
                  {count}
                </span>
              </div>
            ))}
          </div>
          )}
          <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '12px' }}>
            Click a row to view count by workspace for that error reason.
          </p>
        </section>
      )}

      {selectedView && (
        <section style={{ marginTop: '32px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px',
            marginBottom: '16px',
          }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>
                {getDrillDownHeading()}
              </h2>
              <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#6b7280' }}>
                {DRILLDOWN_CONTEXT_LINE}
              </p>
              {selectedView === 'errorDistribution' && selectedErrorReason === 'Enrichment failed' && (
                <p style={{ margin: '10px 0 0', fontSize: '13px', color: '#6b7280' }}>
                  <a
                    href="#enrichments"
                    onClick={(e) => {
                      e.preventDefault();
                      handleEnrichmentsClickFromErrorDistribution(e, 'Enrichment failed');
                    }}
                    style={{ color: '#059669', fontWeight: 500, textDecoration: 'none' }}
                    onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                  >
                    Click to know about enrichments that have enrichment failed
                  </a>
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => { setSelectedView(null); setRecordLogs([]); setExpandedRowKey(null); setBreakdownByKey({}); }}
              style={{
                padding: '6px 14px',
                fontSize: '13px',
                color: '#6b7280',
                background: '#f3f4f6',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              Clear
            </button>
          </div>
          <div style={{
            backgroundColor: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}>
            {logsLoading ? (
              <div style={{ textAlign: 'center', padding: '32px', color: '#6b7280', fontSize: '14px' }}>Loading…</div>
            ) : (
              <>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                      {showEnrichmentBreakdown && <th style={{ width: '44px', padding: '12px 8px', textAlign: 'center', fontWeight: 600, color: '#374151' }}></th>}
                      <th style={{ padding: '14px 20px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Slug</th>
                      <th style={{ padding: '14px 20px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>{getCountColumnLabel()}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recordLogs.map((row, i) => {
                      const rowKey = row.account_id ?? row.slug ?? row.accountId ?? i;
                      const isExpanded = showEnrichmentBreakdown && expandedRowKey === rowKey;
                      const breakdown = breakdownByKey[rowKey];
                      const isLoadingBreakdown = loadingBreakdownFor === rowKey;
                      return (
                        <React.Fragment key={rowKey}>
                          <tr
                            style={{
                              borderBottom: isExpanded ? 'none' : (i < recordLogs.length - 1 ? '1px solid #f3f4f6' : 'none'),
                              backgroundColor: isExpanded ? '#f8fafc' : undefined,
                            }}
                          >
                            {showEnrichmentBreakdown && (
                              <td style={{ padding: '12px 8px', textAlign: 'center', verticalAlign: 'middle' }}>
                                <button
                                  type="button"
                                  onClick={(e) => toggleRowExpand(e, row)}
                                  disabled={isLoadingBreakdown}
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    background: '#fff',
                                    cursor: isLoadingBreakdown ? 'wait' : 'pointer',
                                    color: '#64748b',
                                    transition: 'transform 0.2s, background 0.2s',
                                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                  }}
                                  onMouseEnter={(e) => { if (!isLoadingBreakdown) e.currentTarget.style.background = '#f1f5f9'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
                                >
                                  {isLoadingBreakdown ? (
                                    <span style={{ fontSize: '12px' }}>⋯</span>
                                  ) : (
                                    <span style={{ fontSize: '14px', lineHeight: 1 }}>▾</span>
                                  )}
                                </button>
                              </td>
                            )}
                            <td style={{ padding: '14px 20px', color: '#111827', fontWeight: 500 }}>{row.slug ?? row.account_id ?? '—'}</td>
                            <td style={{ padding: '14px 20px', textAlign: 'right', color: '#374151', fontVariantNumeric: 'tabular-nums' }}>{row.count ?? '—'}</td>
                          </tr>
                          {isExpanded && (
                            <tr style={{ backgroundColor: '#f8fafc', borderBottom: i < recordLogs.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
                              <td colSpan={3} style={{ padding: '0 20px 16px 56px', verticalAlign: 'top' }}>
                                <div style={{ padding: '16px', backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', marginTop: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                                  {breakdown === undefined ? (
                                    <div style={{ color: '#64748b', fontSize: '13px' }}>Loading…</div>
                                  ) : breakdown === null || (Array.isArray(breakdown) && breakdown.length === 0) ? (
                                    <div style={{ color: '#64748b', fontSize: '13px' }}>No enrichment breakdown for this workspace.</div>
                                  ) : (
                                    renderDataContent(breakdown)
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
                {recordLogs.length === 0 && !logsLoading && (
                  <div style={{ textAlign: 'center', padding: '32px', color: '#9ca3af', fontSize: '14px' }}>No data for this selection.</div>
                )}
                {recordLogs.length > 0 && (logsPagination.total === null || logsPagination.total > recordLogs.length) && (
                  <div style={{ padding: '16px 20px', borderTop: '1px solid #e5e7eb', backgroundColor: '#f9fafb', textAlign: 'center' }}>
                    <button
                      type="button"
                      disabled={loadingMoreLogs}
                      onClick={loadMoreLogs}
                      style={{
                        padding: '8px 20px',
                        backgroundColor: '#2563eb',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: loadingMoreLogs ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    >
                      {loadingMoreLogs ? 'Loading…' : `Load more${logsPagination.total != null ? ` (${recordLogs.length} / ${logsPagination.total})` : ''}`}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      )}

      {showEnrichmentsModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(15, 23, 42, 0.4)',
            padding: '24px',
          }}
          onClick={() => setShowEnrichmentsModal(false)}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '16px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              maxWidth: '560px',
              width: '100%',
              maxHeight: '85vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#0f172a' }}>
                Enrichments — {enrichmentsStatType === 'running' ? 'Currently processing' : 'Completed successfully'}
              </h3>
              <button
                type="button"
                onClick={() => setShowEnrichmentsModal(false)}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  background: '#fff',
                  cursor: 'pointer',
                  fontSize: '18px',
                  color: '#64748b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '24px', overflow: 'auto', flex: 1 }}>
              {overallVendorsLoading ? (
                <div style={{ textAlign: 'center', padding: '32px', color: '#64748b', fontSize: '14px' }}>Loading enrichments…</div>
              ) : !overallVendorsData || (Array.isArray(overallVendorsData) && overallVendorsData.length === 0) ? (
                <div style={{ textAlign: 'center', padding: '32px', color: '#64748b', fontSize: '14px' }}>No enrichment data for this selection.</div>
              ) : (
                renderDataContent(overallVendorsData)
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
